import React, { useState, useRef } from "react";
import {
  X,
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Save,
} from "lucide-react";
import { Button } from "../ui/button";
import { maintenanceService } from "../../services/maintenance/planMaestroService";
import toast from "react-hot-toast";

interface ImportPreviewItem {
  codigo_interno: string;
  nombre: string;
  descripcion: string;
  categoria: string;
  tipo: "CONSUMIBLE" | "SERVICIO";
  unidad_medida: string;
  precio_unitario: number;
  stock_minimo: number;
  especificaciones: string;
}

interface ImportError {
  row: number;
  data: any;
  errors: string[];
}

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ItemCatalogoImportModal: React.FC<ImportModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewData, setPreviewData] = useState<ImportPreviewItem[]>([]);
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef<any>(null);

  const startProgress = () => {
    setProgress(0);
    if (progressInterval.current) clearInterval(progressInterval.current);
    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 92) return prev;
        return prev + Math.random() * 8;
      });
    }, 250);
  };

  const finishProgress = () => {
    if (progressInterval.current) clearInterval(progressInterval.current);
    setProgress(100);
  };

  if (!isOpen) return null;

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (!validTypes.includes(selectedFile.type)) {
      toast.error("Formato no válido. Use Excel (.xlsx, .xls) o CSV");
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error("El archivo debe ser menor a 5 MB");
      return;
    }

    setFile(selectedFile);
    processFile(selectedFile);
  };

  const processFile = async (file: File) => {
    setLoading(true);
    startProgress();
    try {
      const result = await maintenanceService.previewImportCatalogo(file);

      if (result.success) {
        setPreviewData(result.data.items);
        setErrors(result.data.errors);
        setSummary(result.data.summary);

        if (result.data.summary.valid === 0) {
          toast.error("No se encontraron registros válidos");
        } else {
          toast.success(
            `${result.data.summary.valid} ítems listos para importar`,
          );
        }
      } else {
        toast.error(result.message || "Error al procesar el archivo");
      }
    } catch (error: any) {
      console.error("Error processing file:", error);
      toast.error(
        error.response?.data?.message || "Error al procesar el archivo",
      );
    } finally {
      finishProgress();
      setTimeout(() => setLoading(false), 500);
    }
  };

  const handleImport = async () => {
    if (previewData.length === 0) {
      toast.error("No hay datos válidos para importar");
      return;
    }

    setImporting(true);
    startProgress();
    try {
      const result = await maintenanceService.importItemsCatalogo(previewData);

      if (result.success) {
        finishProgress();
        toast.success(result.message);
        setTimeout(() => {
          onSuccess();
          handleClose();
        }, 600);
      } else {
        finishProgress();
        toast.error(result.message || "Error al importar");
      }
    } catch (error: any) {
      finishProgress();
      console.error("Error importing:", error);
      toast.error(
        error.response?.data?.message || "Error al importar los ítems",
      );
    } finally {
      setTimeout(() => setImporting(false), 800);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await maintenanceService.downloadTemplateCatalogo();

      // El archivo ahora es .xlsx (Excel) en lugar de .csv
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      // Extraer el nombre del archivo desde los headers si existe
      const contentDisposition = response.headers["content-disposition"];
      let filename = "plantilla_items_catalogo.xlsx";

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/"/g, "");
        }
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success("Plantilla Excel descargada");
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Error al descargar plantilla");
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreviewData([]);
    setErrors([]);
    setSummary(null);
    onClose();
  };

  const removeItem = (index: number) => {
    setPreviewData((prev) => prev.filter((_, i) => i !== index));
    setSummary((prev: any) => ({
      ...prev,
      valid: prev.valid - 1,
      total: prev.total - 1,
    }));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white w-full max-w-7xl rounded-3xl shadow-2xl border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-slate-900 px-8 py-6 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/10 rounded-2xl">
              <FileSpreadsheet className="text-white" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-black text-white uppercase tracking-wider">
                Importación Masiva de Ítems
              </h2>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-0.5">
                Carga Excel (.xlsx) o CSV
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/10 text-white/50 hover:text-white rounded-xl transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6">
          {/* Step 1: Upload */}
          {!file && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                  Paso 1: Cargar Archivo
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={downloadTemplate}
                  className="h-9 px-4 text-xs font-black uppercase tracking-wider text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                >
                  <Download size={14} />
                  Descargar Plantilla Excel
                </Button>
              </div>

              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                  dragActive
                    ? "border-indigo-500 bg-indigo-50"
                    : "border-slate-200 hover:border-indigo-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex flex-col items-center gap-4">
                  <div className="p-4 bg-slate-100 rounded-2xl">
                    <Upload className="text-slate-400" size={32} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      Arrastra tu archivo aquí o{" "}
                      <label className="text-indigo-600 cursor-pointer hover:text-indigo-700">
                        explora
                        <input
                          type="file"
                          className="hidden"
                          accept=".xlsx,.xls,.csv"
                          onChange={(e) =>
                            e.target.files?.[0] &&
                            handleFileSelect(e.target.files[0])
                          }
                        />
                      </label>
                    </p>
                    <p className="text-xs text-slate-500 mt-2">
                      Formatos: Excel (.xlsx, .xls) o CSV • Máximo 5 MB
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                <h4 className="text-xs font-black text-blue-900 uppercase tracking-wider mb-2">
                  Formato del Archivo
                </h4>
                <div className="text-xs text-blue-700 space-y-1">
                  <p>
                    • <strong>codigo_interno</strong>: Código único del ítem
                  </p>
                  <p>
                    • <strong>nombre</strong>: Nombre del ítem
                  </p>
                  <p>
                    • <strong>categoria</strong>: FILTROS, LUBRICANTES, FRENOS,
                    etc.
                  </p>
                  <p>
                    • <strong>tipo</strong>: CONSUMIBLE o SERVICIO
                  </p>
                  <p>
                    • <strong>unidad_medida</strong>: UND, L, GAL, KG, SERV,
                    HORA
                  </p>
                  <p>
                    • <strong>precio_unitario</strong>: Precio de referencia
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-6 w-full max-w-md mx-auto animate-in fade-in duration-300">
              <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-100 border-t-indigo-600"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <FileSpreadsheet className="text-indigo-600 animate-pulse" size={24} />
                </div>
              </div>
              
              <div className="w-full space-y-3">
                <div className="flex justify-between items-end">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Procesando y validando catálogo...
                  </p>
                  <span className="text-xl font-black text-indigo-600">{Math.round(progress)}%</span>
                </div>
                
                <div className="h-2.5 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                  <div 
                    className="h-full bg-indigo-600 transition-all duration-300 ease-out shadow-[0_0_10px_rgba(79,70,229,0.4)]"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                
                <p className="text-[9px] text-slate-400 text-center uppercase font-bold tracking-widest">
                  Verificando stock, códigos y precios referenciales
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Preview */}
          {file && !loading && summary && (
            <div className="space-y-4 relative">
               {importing && (
                  <div className="absolute -top-6 left-0 right-0 h-1.5 z-50 bg-slate-100 rounded-full overflow-hidden shadow-sm">
                      <div 
                        className="h-full bg-indigo-600 transition-all duration-300 ease-out shadow-[0_0_15px_rgba(79,70,229,0.6)]" 
                        style={{ width: `${progress}%` }}
                      />
                  </div>
               )}
              {/* Summary */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest">
                  Paso 2: Revisar y Confirmar
                </h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider">
                    <CheckCircle2 className="text-emerald-600" size={16} />
                    <span className="text-emerald-600">
                      {summary.valid} Válidos
                    </span>
                  </div>
                  {summary.invalid > 0 && (
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider">
                      <AlertCircle className="text-rose-600" size={16} />
                      <span className="text-rose-600">
                        {summary.invalid} Errores
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 max-h-48 overflow-y-auto">
                  <h4 className="text-xs font-black text-rose-900 uppercase tracking-wider mb-3">
                    Registros con Errores ({errors.length})
                  </h4>
                  <div className="space-y-2">
                    {errors.map((error, idx) => (
                      <div
                        key={idx}
                        className="text-xs bg-white rounded-lg p-3 border border-rose-100"
                      >
                        <p className="font-black text-rose-700">
                          Fila {error.row}
                        </p>
                        <p className="text-slate-600 mt-1">
                          {error.data.codigo_interno} - {error.data.nombre}
                        </p>
                        <ul className="mt-2 space-y-1 text-rose-600">
                          {error.errors.map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {previewData.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto max-h-96">
                    <table className="w-full text-left border-collapse">
                      <thead className="sticky top-0 bg-slate-900 z-10">
                        <tr>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Código
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Nombre
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Categoría
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            Tipo
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider">
                            U.M.
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider text-right">
                            Precio
                          </th>
                          <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-wider text-center">
                            Acción
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {previewData.map((item, index) => (
                          <tr
                            key={index}
                            className="hover:bg-slate-50 transition-colors"
                          >
                            <td className="px-4 py-3 text-xs font-bold text-slate-900">
                              {item.codigo_interno}
                            </td>
                            <td className="px-4 py-3 text-xs text-slate-700">
                              {item.nombre}
                            </td>
                            <td className="px-4 py-3 text-[10px] font-black text-slate-500 uppercase">
                              {item.categoria}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-2 py-1 rounded-full text-[9px] font-black uppercase ${
                                  item.tipo === "SERVICIO"
                                    ? "bg-amber-100 text-amber-700"
                                    : "bg-indigo-100 text-indigo-700"
                                }`}
                              >
                                {item.tipo}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[10px] font-black text-slate-600">
                              {item.unidad_medida}
                            </td>
                            <td className="px-4 py-3 text-xs font-bold text-slate-900 text-right">
                              ${item.precio_unitario.toFixed(2)}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => removeItem(index)}
                                className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                                title="Remover"
                              >
                                <Trash2 size={14} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-50 border-t border-slate-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {file && (
              <button
                onClick={() => {
                  setFile(null);
                  setPreviewData([]);
                  setErrors([]);
                  setSummary(null);
                }}
                className="text-xs font-black uppercase tracking-wider text-slate-500 hover:text-slate-900 transition-colors"
              >
                Cambiar Archivo
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="h-12 px-6 font-black text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900"
            >
              Cancelar
            </Button>
            {previewData.length > 0 && (
              <Button
                onClick={handleImport}
                disabled={importing}
                className="h-12 px-10 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl shadow-lg shadow-indigo-200 border-none font-black text-xs uppercase tracking-widest transition-all hover:scale-[1.02] active:scale-[0.98]"
              >
                {importing ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    <span>Importando...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Save size={18} />
                    <span>Importar {previewData.length} Ítems</span>
                  </div>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ItemCatalogoImportModal;
